import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { isConfirmationOpenVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";

const useConfirm = (title: string, message: string) => {
  const [memoizedTitle, setTitle] = useState(title);
  const [memoizedMessage, setMessage] = useState(message);
  const confirmButtonRef = useRef(null);

  const [promise, setPromise] = useState<{
    resolve: (val: boolean | PromiseLike<boolean>) => void;
  } | null>(null);

  const confirm = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        setPromise({ resolve });
      }),
    [],
  );

  const handleClose = () => {
    setPromise(null);
  };

  const handleConfirm = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      promise?.resolve(true);
      handleClose();
    },
    [promise],
  );

  const handleCancel = useCallback(() => {
    promise?.resolve(false);
    handleClose();
  }, [promise]);

  useEffect(() => {
    isConfirmationOpenVar(promise !== null);
  }, [promise]);

  const ConfirmationDialog = useMemo(
    () => () => (
      <Modal
        open={promise !== null}
        onClose={handleCancel}
        alignedTop
        className="z-tooltip"
        initialFocus={confirmButtonRef}
      >
        <div className="p-6 flex flex-col gap-4">
          <ModalTitle onClose={handleCancel}>{memoizedTitle}</ModalTitle>
          <div>{memoizedMessage}</div>
          <div className="mt-6 flex items-center gap-2 justify-end">
            <Button
              theme={buttonTheme.text}
              onClick={handleCancel}
              text="Cancel"
            />
            <Button
              ref={confirmButtonRef}
              theme={buttonTheme.primary}
              onClick={handleConfirm}
              aria-label="Confirm modal confirm button"
              text="Confirm"
            />
          </div>
        </div>
      </Modal>
    ),
    [handleCancel, handleConfirm, memoizedMessage, promise, memoizedTitle],
  );
  return { ConfirmationDialog, confirm, setTitle, setMessage };
};

export default useConfirm;
